import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Image, Link2, Loader2, Package, Search, Trash2, UploadCloud, X } from 'lucide-react';
import { mediaApi, type MediaAsset } from '@/api/mediaApi';
import { productsApi } from '@/api/productsApi';
import { suppliesApi } from '@/api/suppliesApi';
import { Button } from '@/components/common/Button';
import { useToast } from '@/hooks/useToast';
import { normalizeUploadedImageUrl } from '@/utils/imageUrls';
import { formatDateTime } from '@/utils/formatters';
import type { Product } from '@/types/api';
import type { Supply } from '@/api/suppliesApi';

const PAGE_SIZE = 36;

const UPLOAD_TYPES = [
  { value: 'ALL', label: 'All Types' },
  { value: 'PRODUCT_IMAGE', label: 'Product Images' },
  { value: 'SUPPLY_IMAGE', label: 'Supply Images' },
  { value: 'VARIANT_IMAGE', label: 'Variant Images' },
  { value: 'INVENTORY_IMAGE', label: 'Inventory Images' },
  { value: 'PRODUCT_DESIGN_FILE', label: 'Product Design Files' },
];

type LinkedFilter = 'ALL' | 'LINKED' | 'UNLINKED';
type AttachEntityType = 'PRODUCT' | 'SUPPLY';

interface AttachTarget {
  id: string;
  name: string;
  sku?: string | null;
  subtitle?: string | null;
}

function formatFileSize(size?: number | null) {
  if (!size) return '-';
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(asset: MediaAsset) {
  return asset.contentType?.startsWith('image/');
}

function typeLabel(type: string) {
  return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export function MediaLibrary() {
  const { success: showSuccess, error: showError } = useToast();
  const [search, setSearch] = useState('');
  const [uploadType, setUploadType] = useState('ALL');
  const [linked, setLinked] = useState<LinkedFilter>('ALL');
  const [page, setPage] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [attachingId, setAttachingId] = useState<string | null>(null);
  const [attachAssetTarget, setAttachAssetTarget] = useState<MediaAsset | null>(null);
  const [attachEntityType, setAttachEntityType] = useState<AttachEntityType>('PRODUCT');
  const [attachSearch, setAttachSearch] = useState('');
  const [selectedTarget, setSelectedTarget] = useState<AttachTarget | null>(null);
  const [attachAsPrimary, setAttachAsPrimary] = useState(true);

  const linkedParam = linked === 'ALL' ? undefined : linked === 'LINKED';
  const query = useQuery({
    queryKey: ['media-library', page, uploadType, linked, search],
    queryFn: () => mediaApi.getAll({
      page,
      size: PAGE_SIZE,
      uploadType: uploadType === 'ALL' ? undefined : uploadType,
      linked: linkedParam,
      search: search.trim() || undefined,
    }),
  });

  const assets = query.data?.content ?? [];
  const attachQueryText = attachSearch.trim();

  const attachResultsQuery = useQuery({
    queryKey: ['media-attach-targets', attachEntityType, attachQueryText],
    enabled: !!attachAssetTarget && attachQueryText.length >= 2,
    queryFn: async (): Promise<AttachTarget[]> => {
      if (attachEntityType === 'PRODUCT') {
        const products = await productsApi.search(attachQueryText);
        return products.map((product: Product) => ({
          id: product.id,
          name: product.name,
          sku: product.sku,
          subtitle: product.productType,
        }));
      }

      const supplies = await suppliesApi.search(attachQueryText);
      return supplies.map((supply: Supply) => ({
        id: supply.id,
        name: supply.name,
        sku: supply.sku,
        subtitle: supply.supplyTypeDisplay || supply.supplyType,
      }));
    },
  });

  const copyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url);
    showSuccess('Media URL copied');
  };

  const deleteAsset = async (asset: MediaAsset) => {
    if (!window.confirm(`Delete "${asset.originalFilename || asset.filename}"? This cannot be undone.`)) {
      return;
    }
    setDeletingId(asset.id);
    try {
      await mediaApi.delete(asset.id);
      showSuccess('Media deleted');
      await query.refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete media');
    } finally {
      setDeletingId(null);
    }
  };

  const backfillUploads = async () => {
    setBackfilling(true);
    try {
      const count = await mediaApi.backfill();
      showSuccess(`Backfilled ${count} existing upload${count === 1 ? '' : 's'}`);
      await query.refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to backfill media');
    } finally {
      setBackfilling(false);
    }
  };

  const attachAsset = async (asset: MediaAsset) => {
    setAttachAssetTarget(asset);
    setAttachEntityType(asset.uploadType.includes('SUPPLY') ? 'SUPPLY' : 'PRODUCT');
    setAttachSearch('');
    setSelectedTarget(null);
    setAttachAsPrimary(true);
  };

  const closeAttachModal = () => {
    setAttachAssetTarget(null);
    setSelectedTarget(null);
    setAttachSearch('');
  };

  const submitAttach = async () => {
    if (!attachAssetTarget || !selectedTarget) return;
    setAttachingId(attachAssetTarget.id);
    try {
      await mediaApi.attach({
        url: attachAssetTarget.url,
        entityType: attachEntityType,
        entityId: selectedTarget.id,
        displayName: selectedTarget.name,
        primary: attachAsPrimary,
      });
      showSuccess('Media attached');
      closeAttachModal();
      await query.refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to attach media');
    } finally {
      setAttachingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-slate-800 to-cyan-950 p-6 text-white shadow-xl">
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-32 w-72 rounded-full bg-amber-300/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/15">
                <Image className="h-7 w-7 text-cyan-200" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-cyan-100/80">Admin</p>
                <h1 className="text-3xl font-bold">Media Library</h1>
              </div>
            </div>
            <p className="mt-4 max-w-2xl text-sm text-slate-200">
              Review uploaded photos and design files, find unlinked media, copy URLs, and clean up assets that are no longer needed.
            </p>
          </div>
          <div className="rounded-2xl bg-white/10 px-4 py-3 text-sm ring-1 ring-white/15">
            <span className="font-semibold">{query.data?.totalElements ?? 0}</span> tracked uploads
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-charcoal-700 dark:bg-charcoal-800">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_220px_180px_auto]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Search filename, original name, or URL..."
              className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 dark:border-charcoal-600 dark:bg-charcoal-900 dark:text-warm-cream"
            />
          </label>
          <select
            value={uploadType}
            onChange={(event) => {
              setUploadType(event.target.value);
              setPage(0);
            }}
            className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm dark:border-charcoal-600 dark:bg-charcoal-900 dark:text-warm-cream"
          >
            {UPLOAD_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select
            value={linked}
            onChange={(event) => {
              setLinked(event.target.value as LinkedFilter);
              setPage(0);
            }}
            className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm dark:border-charcoal-600 dark:bg-charcoal-900 dark:text-warm-cream"
          >
            <option value="ALL">All Media</option>
            <option value="LINKED">Linked</option>
            <option value="UNLINKED">Unlinked</option>
          </select>
          <Button variant="secondary" onClick={() => query.refetch()} disabled={query.isFetching}>
            {query.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3 dark:border-charcoal-700">
          <p className="text-xs text-gray-600 dark:text-warm-sand">
            Backfill scans the upload folder and adds any older files that are missing from the library.
          </p>
          <Button variant="secondary" size="sm" onClick={backfillUploads} disabled={backfilling}>
            {backfilling ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1 h-4 w-4" />}
            Backfill Existing Uploads
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-dashed border-gray-300 dark:border-charcoal-700">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
        </div>
      ) : assets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-charcoal-700 dark:bg-charcoal-800">
          <UploadCloud className="mx-auto h-10 w-10 text-gray-400" />
          <h2 className="mt-3 text-lg font-semibold text-gray-900 dark:text-warm-cream">No media found</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-warm-sand">Try changing the filters or upload a new product/supply photo.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {assets.map((asset) => {
            const imageUrl = normalizeUploadedImageUrl(asset.url);
            return (
              <article key={asset.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-charcoal-700 dark:bg-charcoal-800">
                <div className="flex h-48 items-center justify-center bg-gray-100 dark:bg-charcoal-900">
                  {isImage(asset) && imageUrl ? (
                    <img src={imageUrl} alt={asset.originalFilename || asset.filename} className="h-full w-full object-contain" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-warm-sand">
                      <UploadCloud className="h-9 w-9" />
                      <span className="text-xs">{asset.contentType || 'File'}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-3 p-4">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="line-clamp-2 text-sm font-semibold text-gray-900 dark:text-warm-cream">
                        {asset.originalFilename || asset.filename}
                      </h3>
                      <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${asset.linked ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300'}`}>
                        {asset.linked ? 'Linked' : 'Unlinked'}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-gray-500 dark:text-warm-sand">{asset.filename}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-warm-sand">
                    <span>{typeLabel(asset.uploadType)}</span>
                    <span className="text-right">{formatFileSize(asset.size)}</span>
                    <span>{asset.uploadedBy || 'Unknown user'}</span>
                    <span className="text-right">{formatDateTime(asset.createdAt)}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={() => copyUrl(asset.url)} className="flex-1">
                      <Copy className="mr-1 h-4 w-4" /> Copy URL
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => attachAsset(asset)} disabled={attachingId === asset.id}>
                      {attachingId === asset.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => deleteAsset(asset)} disabled={deletingId === asset.id}>
                      {deletingId === asset.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {(query.data?.totalPages ?? 0) > 1 && (
        <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-4 text-sm dark:border-charcoal-700 dark:bg-charcoal-800">
          <span className="text-gray-600 dark:text-warm-sand">
            Page {page + 1} of {query.data?.totalPages ?? 1}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>
              Previous
            </Button>
            <Button variant="secondary" size="sm" disabled={page + 1 >= (query.data?.totalPages ?? 1)} onClick={() => setPage((value) => value + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {attachAssetTarget && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-gray-900/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-t-3xl bg-white shadow-2xl dark:bg-charcoal-900 sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-5 dark:border-charcoal-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-cream">Attach Media</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-warm-sand">
                  Search and select the product or supply this file belongs to.
                </p>
              </div>
              <button
                type="button"
                onClick={closeAttachModal}
                className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 dark:text-warm-sand dark:hover:bg-charcoal-800"
                aria-label="Close attach media"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="flex gap-2 rounded-2xl bg-gray-100 p-1 dark:bg-charcoal-800">
                {(['PRODUCT', 'SUPPLY'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setAttachEntityType(type);
                      setSelectedTarget(null);
                    }}
                    className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                      attachEntityType === type
                        ? 'bg-white text-cyan-700 shadow-sm dark:bg-charcoal-700 dark:text-cyan-300'
                        : 'text-gray-600 hover:text-gray-900 dark:text-warm-sand dark:hover:text-warm-cream'
                    }`}
                  >
                    {type === 'PRODUCT' ? 'Product' : 'Supply'}
                  </button>
                ))}
              </div>

              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={attachSearch}
                  onChange={(event) => {
                    setAttachSearch(event.target.value);
                    setSelectedTarget(null);
                  }}
                  placeholder={`Search ${attachEntityType === 'PRODUCT' ? 'products' : 'supplies'} by name or SKU...`}
                  className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-9 pr-3 text-sm text-gray-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 dark:border-charcoal-600 dark:bg-charcoal-950 dark:text-warm-cream"
                  autoFocus
                />
              </label>

              <div className="max-h-72 overflow-y-auto rounded-2xl border border-gray-200 dark:border-charcoal-700">
                {attachQueryText.length < 2 ? (
                  <div className="p-6 text-center text-sm text-gray-600 dark:text-warm-sand">
                    Type at least 2 characters to search.
                  </div>
                ) : attachResultsQuery.isFetching ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
                  </div>
                ) : (attachResultsQuery.data ?? []).length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-600 dark:text-warm-sand">
                    No {attachEntityType.toLowerCase()}s found for "{attachQueryText}".
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-charcoal-700">
                    {(attachResultsQuery.data ?? []).map((target) => (
                      <button
                        key={target.id}
                        type="button"
                        onClick={() => setSelectedTarget(target)}
                        className={`flex w-full items-center gap-3 p-4 text-left transition ${
                          selectedTarget?.id === target.id
                            ? 'bg-cyan-50 dark:bg-cyan-500/10'
                            : 'hover:bg-gray-50 dark:hover:bg-charcoal-800'
                        }`}
                      >
                        <div className="rounded-xl bg-cyan-100 p-2 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-300">
                          <Package className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-900 dark:text-warm-cream">{target.name}</p>
                          <p className="truncate text-xs text-gray-600 dark:text-warm-sand">
                            {target.sku || 'No SKU'}{target.subtitle ? ` - ${target.subtitle}` : ''}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-warm-sand">
                <input
                  type="checkbox"
                  checked={attachAsPrimary}
                  onChange={(event) => setAttachAsPrimary(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                />
                Make this the primary image
              </label>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 p-5 dark:border-charcoal-700 sm:flex-row sm:justify-end">
              <Button variant="secondary" onClick={closeAttachModal}>Cancel</Button>
              <Button
                onClick={submitAttach}
                disabled={!selectedTarget || attachingId === attachAssetTarget.id}
              >
                {attachingId === attachAssetTarget.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Attach Media'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
